import { Table, Typography } from "antd";
import { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import axios from "api/axios";
import { useDpatIndicatorDataSource } from "hooks/useDpatIndicatorDataSource";
import DpatSubIndicatorSection from "../setup/DpatSubIndicatorSection";
import DpatIndicatorCmsIntro from "../setup/DpatIndicatorCmsIntro";
import MaxScoreDisplay from "../setup/MaxScoreDisplay";
import ActualScoreDisplay from "../setup/ActualScoreDisplay";
import IndicatorScoreSection from "../setup/IndicatorScoreSection";
import Comment from "components/Comments";
import {
  formatDataGeneral,
  getAttributeValue,
  getFileLinkIfExist,
  getYearFromDateValue,
} from "utils/utils";
import { transformDpatTableRows, rowsHaveAllEvidenceLinks, isEvidenceLinkUploaded } from "utils/renderDpatEvidenceLink";
import { isDpatIncidentYear, isDpatScoreRelaxationExempt } from "utils/dpatScoreRelaxation";
import { buildSearchableColumns, getDpatTablePagination } from "utils/dpatSearchableTable";

const AAP_PROGRAM = "ArLnAxhykoz";
const FOOD_VENDOR_PROGRAM = "abiQOocP8YA";
const PUBLICATIONS_PROGRAM = "X5kGqVpbGoN";
const DOCUMENTS_HUB_PROGRAM = "SERtHvYVHRT";
const SCHOOL_PROGRAM = "g27TeeehRQC";
const FOOD_VENDOR_REPORT_STAGE = "DillnCMP1s1";
const SCHOOL_PERIODIC_STAGE = "z6mWkIfypaw";
const NUTRITION_ORIENTATION_ELEMENT = "dEh3AqIrbCn";
const SCHOOL_FEEDING_ELEMENT = "PchURnKOaX1";
const PUBLICATION_EVIDENCE_FILE = "xjRCTFFiMA3";
const ENV_HEALTH_DOC_TYPE = "Environmental Health Reports";
const MONITORING_REPORT_FILE = "MjPmcbueEdU";
const NUTRITION_PUBLICATION_TYPE = "Nutrition Services";
const NUTRITION_ACTIVITY_SOURCE = "Nutrition-Oriented Intervention Activity";

function buildFileUrl(eventId, dataElementId) {
  return `https://dddp.gov.gh/api/events/files?eventUid=${eventId}&dataElementUid=${dataElementId}`;
}

function normalizeWebsiteUrl(url) {
  if (!url || url === "N/A") {
    return "Not Uploaded";
  }
  const trimmed = String(url).trim();
  if (!trimmed) {
    return "Not Uploaded";
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

/** Orientation date comes from the Food Vendor report-stage event (prefer occurredAt). */
function getOrientationDateRaw(event) {
  return event?.occurredAt || event?.scheduledAt || null;
}

function formatOrientationDate(rawDate) {
  if (!rawDate) {
    return "N/A";
  }
  return String(rawDate).split("T")[0];
}

function isOrientationOnOrBeforeSelectedYear(rawDate, selectedYear) {
  const yearNum = Number(selectedYear);
  if (!Number.isFinite(yearNum)) {
    return false;
  }

  const orientationYear = getYearFromDateValue(rawDate);
  if (isDpatScoreRelaxationExempt()) {
    return isDpatIncidentYear(orientationYear, yearNum);
  }
  return orientationYear != null && orientationYear <= yearNum;
}

function resolveEnvironmentalHealthReport(documents, docReports) {
  const envReports = formatDataGeneral(documents, "DPAT | M&E Tool Type", ENV_HEALTH_DOC_TYPE) || [];

  for (const document of envReports) {
    const eventId = getFileLinkIfExist(docReports, MONITORING_REPORT_FILE, document.trackedEntity);
    if (eventId) {
      return {
        title: getAttributeValue("Name", document),
        evidence: buildFileUrl(eventId, MONITORING_REPORT_FILE),
      };
    }
  }

  return {
    title: "Environmental Health Report",
    evidence: "Not Uploaded",
  };
}

const NutritionIntervention = forwardRef(({
  year,
  districtId,
  hideComment,
  savedAssessmentData,
  assessmentStartLoading,
  isAssessmentStarted,
}, ref) => {
  const [scorei, setScorei] = useState(0);
  const [maxScore, setMaxScore] = useState(2);
  const [aapSummary, setAapSummary] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [publications, setPublications] = useState([]);
  const [environmentalHealthReport, setEnvironmentalHealthReport] = useState([]);
  const [schoolFeedingSummary, setSchoolFeedingSummary] = useState([]);
  const [schoolFeedingList, setSchoolFeedingList] = useState([]);

  const { Title } = Typography;

  useImperativeHandle(ref, () => ({
    getData: () => ({
      indicator: "SDI4",
      area: "Social Protection, Gender and Nutrition",
      maxScore,
      data: {
        aap: aapSummary,
        vendors,
        publications,
        environmentalHealthReport,
        schoolFeeding: schoolFeedingSummary,
        schoolFeedingList,
      },
      scorei,
    }),
  }));

  const DPAT_SLUG = "sdi4.0-4.5";

  const loadFromDhis2 = useCallback(() => {
    const aapRequest = axios.get(
      `/tracker/trackedEntities?orgUnit=${districtId}&program=${AAP_PROGRAM}&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=5000`,
    );
    const vendorRequest = axios.get(
      `/tracker/trackedEntities?orgUnit=${districtId}&program=${FOOD_VENDOR_PROGRAM}&pageSize=5000`,
    );
    const publicationRequest = axios.get(
      `/tracker/trackedEntities?orgUnit=${districtId}&program=${PUBLICATIONS_PROGRAM}&pageSize=5000`,
    );
    const documentRequest = axios.get(
      `/tracker/trackedEntities?orgUnit=${districtId}&program=${DOCUMENTS_HUB_PROGRAM}&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=5000`,
    );
    const schoolRequest = axios.get(
      `/tracker/trackedEntities?orgUnit=${districtId}&program=${SCHOOL_PROGRAM}&pageSize=5000`,
    );

    Promise.all([aapRequest, vendorRequest, publicationRequest, documentRequest, schoolRequest])
      .then(([aapResult, vendorResult, publicationResult, documentResult, schoolResult]) => {
        const aapInstances = aapResult.data?.instances || [];
        const vendorsList = vendorResult.data?.instances || [];
        const publicationInstances = publicationResult.data?.instances || [];
        const documents = documentResult.data?.instances || [];
        const schools = schoolResult.data?.instances || [];

        const aapEventsPromise = aapInstances.length > 0
          ? axios.get(`/tracker/events?program=${AAP_PROGRAM}&orgUnit=${districtId}&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=5000`)
          : Promise.resolve({ data: { instances: [] } });
        const vendorEventsPromise = vendorsList.length > 0
          ? axios.get(`/tracker/events?program=${FOOD_VENDOR_PROGRAM}&orgUnit=${districtId}&pageSize=5000`)
          : Promise.resolve({ data: { instances: [] } });
        const publicationEventsPromise = publicationInstances.length > 0
          ? axios.get(`/tracker/events?program=${PUBLICATIONS_PROGRAM}&orgUnit=${districtId}&pageSize=5000`)
          : Promise.resolve({ data: { instances: [] } });
        const documentEventsPromise = documents.length > 0
          ? axios.get(`/tracker/events?program=${DOCUMENTS_HUB_PROGRAM}&orgUnit=${districtId}&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=5000`)
          : Promise.resolve({ data: { instances: [] } });
        const schoolEventsPromise = schools.length > 0
          ? axios.get(`/tracker/events?program=${SCHOOL_PROGRAM}&orgUnit=${districtId}&startDate=${year}-01-01&endDate=${year}-12-31&pageSize=5000`)
          : Promise.resolve({ data: { instances: [] } });

        return Promise.all([
          aapEventsPromise,
          vendorEventsPromise,
          publicationEventsPromise,
          documentEventsPromise,
          schoolEventsPromise,
        ]).then(([aapEvents, vendorEvents, publicationEvents, documentEvents, schoolEvents]) => {
          const nutritionPublications = formatDataGeneral(
            publicationInstances,
            "Document Published",
            NUTRITION_PUBLICATION_TYPE,
          ) || [];
          const nutritionOrientedIntervention = formatDataGeneral(
            aapInstances,
            "Activity Source",
            NUTRITION_ACTIVITY_SOURCE,
          ) || [];
          const publicationReports = publicationEvents.data?.instances || [];
          const vendorReports = vendorEvents.data?.instances || [];
          const docReports = documentEvents.data?.instances || [];
          const schoolReports = schoolEvents.data?.instances || [];
          const publicSchools = formatDataGeneral(schools, "Type", "Public") || [];

          const websiteUrl = nutritionPublications.length > 0
            ? normalizeWebsiteUrl(getAttributeValue("Website", nutritionPublications[0]))
            : "Not Uploaded";

          setAapSummary([{
            key: "nutrition-aap-summary",
            aapTotal: aapInstances.length,
            aapNutrition: nutritionOrientedIntervention.length,
            publication: nutritionPublications.length > 0 ? "YES" : "NO",
            websiteEvidence: websiteUrl,
          }]);

          const vendorRows = [];

          vendorsList.forEach((vendor, index) => {
            const currentReport = vendorReports.find(
              (report) => report.trackedEntity === vendor.trackedEntity
                && report.programStage === FOOD_VENDOR_REPORT_STAGE,
            );
            const oriented = currentReport?.dataValues?.some(
              (value) => value.dataElement === NUTRITION_ORIENTATION_ELEMENT && value.value === "true",
            );
            const orientationRaw = oriented ? getOrientationDateRaw(currentReport) : null;
            const orientedForYear = oriented
              && isOrientationOnOrBeforeSelectedYear(orientationRaw, year);

            // Only show vendors with an orientation date on or before the selected year.
            if (!orientedForYear) {
              return;
            }

            vendorRows.push({
              key: vendor.trackedEntity || `vendor-${index}`,
              name: getAttributeValue("DCACT | Food Vendors - Name of vendor / Business", vendor),
              phone: getAttributeValue("Phone", vendor),
              orientationDate: formatOrientationDate(orientationRaw),
              orientation: "YES",
            });
          });

          const publicationRows = nutritionPublications.map((document, index) => {
            const reportLink = getFileLinkIfExist(publicationReports, PUBLICATION_EVIDENCE_FILE, document.trackedEntity);

            return {
              key: document.trackedEntity || `publication-${index}`,
              availability: reportLink ? "YES" : "NO",
              docReference: getAttributeValue("Document Reference ", document),
              channel: getAttributeValue("Publication Channel", document),
              webSiteLink: normalizeWebsiteUrl(getAttributeValue("Website", document)),
              evidence: reportLink
                ? buildFileUrl(reportLink, PUBLICATION_EVIDENCE_FILE)
                : "Not Uploaded",
            };
          });

          const envReport = resolveEnvironmentalHealthReport(documents, docReports);
          setEnvironmentalHealthReport([{
            key: "environmental-health-report",
            title: envReport.title,
            evidence: envReport.evidence,
          }]);

          const feedingRows = [];
          let feedingCount = 0;

          publicSchools.forEach((school, index) => {
            const currentReport = schoolReports.find(
              (report) => report.trackedEntity === school.trackedEntity
                && report.programStage === SCHOOL_PERIODIC_STAGE,
            );
            const enrolledInFeeding = currentReport?.dataValues?.some(
              (value) => value.dataElement === SCHOOL_FEEDING_ELEMENT && value.value === "true",
            );

            if (enrolledInFeeding) {
              feedingCount++;
            }

            feedingRows.push({
              key: school.trackedEntity || `school-${index}`,
              no: index + 1,
              school: getAttributeValue("Name of School", school),
              community: getAttributeValue("Community", school),
              schoolFeeding: enrolledInFeeding ? "YES" : "NO",
            });
          });

          setSchoolFeedingSummary([{
            key: "school-feeding-summary",
            totalPublicSchools: publicSchools.length,
            schoolsInFeedingProgram: feedingCount,
          }]);
          setSchoolFeedingList(
            feedingRows
              .filter((row) => row.schoolFeeding === "YES")
              .map((row, index) => ({ ...row, no: index + 1 })),
          );
          setVendors(vendorRows);
          setPublications(publicationRows);

          const publicationsComplete = rowsHaveAllEvidenceLinks(publicationRows, ["evidence"]);
          const envReportComplete = rowsHaveAllEvidenceLinks(
            [{ evidence: envReport.evidence }],
            ["evidence"],
          );
          const qualifiesOnData = vendorRows.length > 0 && publicationRows.length > 0;

          setScorei(
            qualifiesOnData && publicationsComplete && envReportComplete ? 2 : 0,
          );
        });
      })
      .catch((err) => console.log(err));
  }, [districtId, year]);

  useDpatIndicatorDataSource({
    isAssessmentStarted,
    savedAssessmentData,
    assessmentStartLoading,
    loadFromDhis2,
    applySnapshot: (snap) => {
      const snapshotData = snap.data || {};
      if (snapshotData.aap) setAapSummary(snapshotData.aap);
      if (snapshotData.vendors) setVendors(snapshotData.vendors);
      if (snapshotData.publications) setPublications(snapshotData.publications);
      if (snapshotData.environmentalHealthReport) setEnvironmentalHealthReport(snapshotData.environmentalHealthReport);
      if (snapshotData.schoolFeeding) setSchoolFeedingSummary(snapshotData.schoolFeeding);
      if (snapshotData.schoolFeedingList) setSchoolFeedingList(snapshotData.schoolFeedingList);
      if (snap.scorei != null) setScorei(snap.scorei);
      if (snap.maxScore != null) setMaxScore(snap.maxScore);
    },
    deps: [year, districtId, loadFromDhis2],
  });

  const aapColumn = [
    { title: "No. of Activities in the AAP of the Assembly", dataIndex: "aapTotal", key: "aapTotal" },
    { title: "No. of Nutrition-oriented interventions", dataIndex: "aapNutrition", key: "aapNutrition" },
    {
      title: "Nutrition-oriented interventions published on Assembly website or notice board (Yes/No)",
      dataIndex: "publication",
      key: "publication",
    },
    {
      title: "Website Evidence",
      dataIndex: "websiteEvidence",
      key: "websiteEvidence",
    },
  ];

  const nutritionColumn = useMemo(
    () => buildSearchableColumns(
      [
        { title: "Vendors", dataIndex: "name", key: "name" },
        { title: "Phone", dataIndex: "phone", key: "phone" },
        { title: "Orientation Date", dataIndex: "orientationDate", key: "orientationDate" },
        { title: "Vendor Nutrition Orientation", dataIndex: "orientation", key: "orientation" },
      ],
      vendors,
      {
        textSearchKeys: ["name", "phone", "orientationDate"],
        selectFilterKeys: ["orientation"],
      },
    ),
    [vendors],
  );

  const publicationColumn = [
    { title: "Nutrition Service Publication Availability (YES/NO)", dataIndex: "availability", key: "availability" },
    { title: "Document Reference", dataIndex: "docReference", key: "docReference" },
    { title: "Web site", dataIndex: "webSiteLink", key: "webSiteLink" },
    { title: "Channel", dataIndex: "channel", key: "channel" },
    { title: "Evidence", dataIndex: "evidence", key: "evidence" },
  ];

  const environmentalHealthColumn = [
    { title: "Report", dataIndex: "title", key: "title" },
    { title: "Evidence", dataIndex: "evidence", key: "evidence" },
  ];

  const schoolFeedingSummaryColumn = [
    { title: "Total Public Schools", dataIndex: "totalPublicSchools", key: "totalPublicSchools" },
    { title: "Schools in School Feeding Programme", dataIndex: "schoolsInFeedingProgram", key: "schoolsInFeedingProgram" },
  ];

  const schoolFeedingListColumn = useMemo(
    () => buildSearchableColumns(
      [
        { title: "No.", dataIndex: "no", key: "no" },
        { title: "School", dataIndex: "school", key: "school" },
        { title: "Community", dataIndex: "community", key: "community" },
        { title: "Enrolled in School Feeding Programme (Yes/No)", dataIndex: "schoolFeeding", key: "schoolFeeding" },
      ],
      schoolFeedingList,
      {
        textSearchKeys: ["school", "community"],
        selectFilterKeys: ["schoolFeeding"],
      },
    ),
    [schoolFeedingList],
  );

  const linkKeys = ["websiteEvidence", "webSiteLink", "evidence"];
  const linkLabels = {
    websiteEvidence: "View Website",
    webSiteLink: "View Website",
    evidence: "View Evidence",
  };

  return (
    <Comment
      data={aapSummary}
      year={year}
      districtId={districtId}
      tableCommentedId={`sdi4.0-4.5-${year}`}
      hideComment={hideComment}
    >
      {({ renderCommentFeedbackFooter }) => (
        <DpatSubIndicatorSection code={DPAT_SLUG}>
          <DpatIndicatorCmsIntro
            year={year}
            slug={DPAT_SLUG}
            dhis2ProgramId={PUBLICATIONS_PROGRAM}
            movDefaultText="Tracker for recording all publications made from the MMDA, including nutrition services, environmental health reports, and school feeding programme records."
          />

          <IndicatorScoreSection>
            <MaxScoreDisplay score={maxScore} />
            <ActualScoreDisplay slug={DPAT_SLUG} label="SDI 4.0-4.5 Actual Score:" score={scorei} />
          </IndicatorScoreSection>

          <Title level={4} style={{ marginTop: "20px" }}>I- Evidence of Nutrition-Oriented Activities in the Assembly</Title>
          <Table
            columns={aapColumn}
            dataSource={transformDpatTableRows(aapSummary, ["websiteEvidence"], linkLabels)}
            pagination={false}
            bordered
            rowKey="key"
          />

          <Title level={4} style={{ marginTop: "20px" }}>II- Evidence of Nutrition Orientations</Title>
          <Table
            columns={nutritionColumn}
            dataSource={vendors}
            pagination={getDpatTablePagination("vendors")}
            bordered
            rowKey="key"
          />

          <Title level={4} style={{ marginTop: "20px" }}>III- Evidence of Nutrition Service Publication</Title>
          <Table
            columns={publicationColumn}
            dataSource={transformDpatTableRows(publications, ["webSiteLink", "evidence"], linkLabels)}
            pagination={false}
            bordered
            rowKey="key"
          />

          <Title level={4} style={{ marginTop: "20px" }}>IV- Environmental Health Report</Title>
          <Table
            columns={environmentalHealthColumn}
            dataSource={transformDpatTableRows(environmentalHealthReport, ["evidence"], linkLabels)}
            pagination={false}
            bordered
            rowKey="key"
          />

          <Title level={4} style={{ marginTop: "20px" }}>V- School Feeding Programme</Title>
          <Table
            columns={schoolFeedingSummaryColumn}
            dataSource={schoolFeedingSummary}
            pagination={false}
            bordered
            rowKey="key"
          />
          <Table
            style={{ marginTop: "16px" }}
            columns={schoolFeedingListColumn}
            dataSource={schoolFeedingList}
            pagination={getDpatTablePagination("schools")}
            bordered
            rowKey="key"
          />
          {!hideComment && renderCommentFeedbackFooter()}
        </DpatSubIndicatorSection>
      )}
    </Comment>
  );
});

export default NutritionIntervention;
